import { Button, CircularProgress } from '@mui/material';

const VARIANT_MAP = {
  base: {
    muiVariant: 'contained',
    sx: {
      height: 56,
      px: 3,
      color: '#ffffff',
      backgroundColor: '#5e894f',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: 'rgb(116, 168, 97)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(184, 134, 11, 0.2)',
      },
      '&:disabled': {
        backgroundColor: '#cccccc',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  contained: {
    muiVariant: 'contained',
    sx: {
      px: 3,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      '&:hover': {
        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
        transform: 'translateY(-1px)',
      },
      '&:disabled': {
        backgroundColor: 'grey.400',
        color: 'grey.600',
        boxShadow: 'none',
        transform: 'none',
      },
    },
  },
  outlined: {
    muiVariant: 'outlined',
    sx: (theme) => ({
      px: 3,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: 'rgba(94, 137, 79, 0.04)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(94, 137, 79, 0.2)',
      },
      '&:disabled': {
        borderColor: 'grey.400',
        color: 'grey.400',
        transform: 'none',
        boxShadow: 'none',
      },
    }),
  },
  warning: {
    muiVariant: 'contained',
    sx: {
      height: 56,
      px: 3,
      color: '#ffffff',
      backgroundColor: '#b8860b',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: '#dba10e',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(184, 134, 11, 0.2)',
      },
      '&:disabled': {
        backgroundColor: '#cccccc',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  danger: {
    muiVariant: 'contained',
    sx: {
      height: 56,
      px: 3,
      color: '#ffffff',
      backgroundColor: '#b8390b',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: 'rgb(221, 70, 15)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(184, 134, 11, 0.2)',
      },
      '&:disabled': {
        backgroundColor: '#cccccc',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  text: {
    muiVariant: 'text',
    sx: (theme) => ({
      px: 3,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: 'rgba(94, 137, 79, 0.04)',
        transform: 'translateY(-1px)',
      },
      '&:disabled': {
        color: 'grey.400',
        transform: 'none',
      },
    }),
  },
  info: {
    muiVariant: 'contained',
    sx: {
      height: 56,
      px: 3,
      color: '#ffffff',
      backgroundColor: '#0b7eb8ff',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: '#0e91d3ff',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(11, 121, 184, 0.2)',
      },
      '&:disabled': {
        backgroundColor: '#cccccc',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
};

const BASE_STYLES = {
  fontSize: { xs: '14px', sm: '16px' },
  py: { xs: 1, sm: 1.5 },
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',
  textTransform: 'none',
};

const resolveVariant = (variant) => {
  const config = VARIANT_MAP[variant];
  if (!config) {
    return {
      muiVariant: variant === 'text' || variant === 'outlined' ? variant : 'contained',
      sx: {},
    };
  }
  return config;
};

const CustomButton = ({
  children,
  buttonIcon: ButtonIcon,
  iconProps = {},
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  isLoading = false,
  sx = {},
  ...props
}) => {
  const variantConfig = resolveVariant(variant);
  const computedSx = typeof variantConfig.sx === 'function' ? (theme) => ({
    ...BASE_STYLES,
    ...variantConfig.sx(theme),
    ...sx,
  }) : {
    ...BASE_STYLES,
    ...variantConfig.sx,
    ...sx,
  };

  const { sx: iconSx = {}, ...restIconProps } = iconProps;
  const resolvedIcon = ButtonIcon ? (
    <ButtonIcon sx={{ fontSize: { xs: 20, sm: 24 }, ...iconSx }} {...restIconProps} />
  ) : undefined;

  const startIcon = isLoading ? (
    <CircularProgress size={20} color="inherit" />
  ) : (
    resolvedIcon
  );

  return (
    <Button
      variant={variantConfig.muiVariant}
      color={color}
      size={size}
      disabled={disabled || isLoading}
      sx={computedSx}
      startIcon={startIcon}
      {...props}
    >
      {children}
    </Button>
  );
};

export default CustomButton;
